import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useParams } from 'react-router-dom'

import { Typography } from 'antd'
import styled from 'styled-components'
import { BarChart } from '@mui/x-charts'
import { chartsTooltipClasses } from '@mui/x-charts/ChartsTooltip'
import { colours } from '@qflow/theme'
import { useUserAccountRole, useUserProjectRole } from 'portal/lib/hooks'
import { icons } from 'portal/lib/primitives'
import * as routes from 'portal/routes'

import {
  Tile,
  TileNumber,
  TileRow,
  Tiles,
  TitleBar,
  TitleBarIcon
} from './commonStyles'

export const Alerts = () => {
  const { accountId } = useParams<{ accountId: string }>()
  const { isAdmin } = useUserAccountRole(accountId)
  // const { isProjectAdmin } = useUserProjectRole(accountId, projectId)
  const { Title } = Typography

  const { projects, isLoading } = useSelector((state: any) => state.projects)

  const lastUsedProjectId = sessionStorage.getItem('last-used-project-id')

  const createAccountSettingsUri = useCallback(
    route => {
      if (accountId) {
        return generatePath(route, {
          accountId
        })
      }
      return ''
    },
    [accountId]
  )
  const createUri = route => {
    if (accountId && projects && projects.length > 0 && !isLoading) {
      return generatePath(route, {
        accountId,
        projectId: lastUsedProjectId ? lastUsedProjectId : projects[0].projectId
      })
    }
    return ''
  }
  const accountSettingsUri = createAccountSettingsUri(routes.ACCOUNT_SETTINGS)
  const alertsMaterialCertificationsUri = createUri(
    routes.ALERTS_MATERIAL_CERTIFICATIONS_TABLE
  )
  const alertsQualityAssuranceUri = createUri(
    routes.ALERTS_QUALITY_ASSURANCE_TABLE
  )
  const alertsSubmissionIssuesUri = createUri(
    routes.ALERTS_SUBMISSION_ISSUES_TABLE
  )
  const alertsWasteComplianceUri = createUri(
    routes.ALERTS_WASTE_COMPLIANCE_TABLE
  )

  // Placeholder data until backend is connected
  const dataForTable = [
    {
      data: [8, 12, 15, 19],
      stack: 'A',
      label: 'Material Certification',
      color: colours.CORE_NAVY
    },
    {
      data: [8, 10, 8, 22],
      stack: 'A',
      label: 'Waste Compliance',
      color: colours.TEAL
    },
    {
      data: [3, 6, 8, 5],
      stack: 'A',
      label: 'Quality Assurance',
      color: colours.CONSTRUCTION_ORANGE
    },
    {
      data: [2, 4, 1, 5],
      stack: 'A',
      label: 'Submission Issues',
      color: colours.YELLOW
    }
  ]

  return (
    <>
      <TitleBar>
        <TitleBarIcon>
          <icons.ShieldTick colour="currentColor" />
        </TitleBarIcon>
        <Title level={4}>Alerts</Title>
      </TitleBar>
      <TwoColumns>
        <RightColumn>
          <Tile
            style={{
              height: '100%',
              paddingLeft: '0',
              paddingRight: '1rem',
              paddingTop: '0.875rem'
            }}
          >
            <BarChart
              series={dataForTable}
              borderRadius={10}
              grid={{ horizontal: true }}
              xAxis={[
                { scaleType: 'band', data: ['Jan', 'Feb', 'Mar', 'Apr'] }
              ]}
              slotProps={{
                legend: {
                  sx: {
                    gap: 1,
                    rowGap: 1
                  }
                },
                tooltip: {
                  sx: {
                    [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.labelCell}`]:
                      {
                        color: colours.CORE_NAVY,
                        fontSize: '0.875rem'
                      },
                    [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.valueCell}`]:
                      {
                        fontSize: '0.875rem'
                      },
                    [`&.${chartsTooltipClasses.root}`]: {
                      borderRadius: '0.625rem'
                    },
                    [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.mark}`]:
                      {
                        borderRadius: '0.25rem',
                        marginTop: '-1px'
                      },
                    [`&.${chartsTooltipClasses.root} .${chartsTooltipClasses.paper}`]:
                      {
                        paddingTop: '0.375rem'
                      }
                  }
                }
              }}
              sx={{
                'height': '100%',
                'maxHeight': '232px',
                '& .MuiChartsLegend-mark': {
                  borderRadius: '0.25rem !important',
                  marginTop: '-1px'
                }
              }}
              margin={{ left: 0, right: 0, top: 16, bottom: 0 }}
            />
          </Tile>
        </RightColumn>
        <LeftColumn>
          <Tiles
            style={{
              gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
              gridTemplateAreas:
                '"one four four four four" "five five five five five"',
              gap: '0.75rem'
            }}
          >
            <Tile
              style={{ gridArea: 'one' }}
              {...(isAdmin
                ? {
                    onClick: () => {
                      window.location.href = `${accountSettingsUri}?view=alertSettings`
                    }
                  }
                : {})}
            >
              <TileRow>
                <Title level={5}>Active</Title>
              </TileRow>
              <TileRow>
                <TileNumber>20</TileNumber>
              </TileRow>
            </Tile>

            <Tile style={{ gridArea: 'four' }}>
              <TileRow>
                <Title level={5}>Most frequent alerts</Title>
              </TileRow>
              <TileRow>
                <ul>
                  <li>No EWC code</li>
                  <li>No facility permit</li>
                  <li>No FSC or PEFC certification</li>
                </ul>
              </TileRow>
            </Tile>
            <Tile>
              <TileRow>
                <Title level={5}>Open</Title>
              </TileRow>
              <TileRow>
                <TileNumber>215</TileNumber>
              </TileRow>
            </Tile>

            <Tile
              onClick={() => {
                window.location.href = alertsMaterialCertificationsUri
              }}
            >
              <TileRow>
                <Title level={5}>Material Certification</Title>
              </TileRow>
              <TileRow>
                <TileNumber>104</TileNumber>
              </TileRow>
            </Tile>

            <Tile
              onClick={() => {
                window.location.href = alertsWasteComplianceUri
              }}
            >
              <TileRow>
                <Title level={5}>Waste Compliance</Title>
              </TileRow>
              <TileRow>
                <TileNumber>100</TileNumber>
              </TileRow>
            </Tile>

            <Tile
              onClick={() => {
                window.location.href = alertsQualityAssuranceUri
              }}
            >
              <TileRow>
                <Title level={5}>Quality Assurance</Title>
              </TileRow>
              <TileRow>
                <TileNumber>9</TileNumber>
              </TileRow>
            </Tile>

            <Tile
              onClick={() => {
                window.location.href = alertsSubmissionIssuesUri
              }}
            >
              <TileRow>
                <Title level={5}>Submission Issues</Title>
              </TileRow>
              <TileRow>
                <TileNumber>2</TileNumber>
              </TileRow>
            </Tile>
          </Tiles>
        </LeftColumn>
      </TwoColumns>
    </>
  )
}

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr calc(50% + 0.5rem);
  gap: 1rem;
`
const LeftColumn = styled.div`
  align-self: stretch;
  justify-self: stretch;
`
const RightColumn = styled.div`
  align-self: stretch;
  justify-self: stretch;
`
